import {
  TicketAuditLogActionType,
  TicketAuditLogEntry,
  TicketAuditLogResponse,
} from '@types';
import { Pagination } from '_components/atoms';
import AdminService from '_services/AdminService';
import HelperMethods from '_utils/HelperMethods';
import React, { useCallback, useEffect, useState } from 'react';

const ACTION_TYPE_OPTIONS: {
  label: string;
  value: '' | TicketAuditLogActionType;
}[] = [
  { label: 'All Actions', value: '' },
  { label: 'Cancelled', value: 'Cancelled' },
  { label: 'Deleted', value: 'Deleted' },
];

const TicketAuditLog: React.FC = () => {
  const [entries, setEntries] = useState<TicketAuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [actionType, setActionType] = useState<'' | TicketAuditLogActionType>(
    '',
  );
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);

  const fetchAuditLog = useCallback(async () => {
    try {
      setLoading(true);
      const response = (await AdminService.getTicketAuditLog({
        pageNumber,
        pageSize,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        actionType: actionType || undefined,
      })) as unknown as TicketAuditLogResponse;

      if (response) {
        setEntries(response.entries || []);
        setTotalRecords(response.pagination?.totalRecords || 0);
        setTotalPages(response.pagination?.totalPages || 0);
        setHasNext(response.pagination?.hasNext || false);
        setHasPrevious(response.pagination?.hasPrevious || false);
      }
    } catch (error) {
      console.error('Error fetching ticket audit log:', error);
    } finally {
      setLoading(false);
    }
  }, [pageNumber, pageSize, dateFrom, dateTo, actionType]);

  useEffect(() => {
    fetchAuditLog();
  }, [fetchAuditLog]);

  const handleFilterChange = () => setPageNumber(1);

  const handlePageChange = (newPage: number) => setPageNumber(newPage);
  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPageNumber(1);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Ticket Audit Log</h1>
          <p className="mt-1 text-sm text-gray-600">
            Who cancelled or deleted a ticket, when, and why
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <div className="text-sm text-gray-600">
            Total Records: <span className="font-semibold">{totalRecords}</span>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex flex-col sm:flex-row gap-3 sm:items-end">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">
            From
          </label>
          <input
            type="date"
            value={dateFrom}
            onChange={e => {
              setDateFrom(e.target.value);
              handleFilterChange();
            }}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">
            To
          </label>
          <input
            type="date"
            value={dateTo}
            onChange={e => {
              setDateTo(e.target.value);
              handleFilterChange();
            }}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">
            Action
          </label>
          <select
            value={actionType}
            onChange={e => {
              setActionType(e.target.value as '' | TicketAuditLogActionType);
              handleFilterChange();
            }}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {ACTION_TYPE_OPTIONS.map(option => (
              <option key={option.label} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <Pagination
        pageNumber={pageNumber}
        totalPages={totalPages}
        pageSize={pageSize}
        hasNext={hasNext}
        hasPrevious={hasPrevious}
        loading={loading}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onReload={fetchAuditLog}
      />

      <div className="hidden md:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date/Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ticket
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Staff Member
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Reason
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {entries.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    No audit log entries found
                  </td>
                </tr>
              ) : (
                entries.map(entry => (
                  <tr
                    key={entry.id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {HelperMethods.formatDateTimeLocal(entry.performedAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                          entry.actionType === 'Deleted'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-amber-100 text-amber-800'
                        }`}
                      >
                        {entry.actionType}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>#{entry.bookedSkatingSessionId}</div>
                      <div className="text-xs text-gray-500">
                        {entry.customerName || 'Unknown customer'}
                        {entry.sessionDate
                          ? ` • ${HelperMethods.formatDate(entry.sessionDate)}`
                          : ''}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {entry.performedByName || 'Unknown'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 max-w-xs">
                      {entry.reason || '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="md:hidden space-y-4">
        {entries.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center text-gray-500">
            No audit log entries found
          </div>
        ) : (
          entries.map(entry => (
            <div
              key={entry.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
            >
              <div className="flex items-center justify-between mb-2">
                <span
                  className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                    entry.actionType === 'Deleted'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-amber-100 text-amber-800'
                  }`}
                >
                  {entry.actionType}
                </span>
                <span className="text-xs text-gray-500">
                  {HelperMethods.formatDateTimeLocal(entry.performedAt)}
                </span>
              </div>
              <div className="text-sm text-gray-900">
                Ticket #{entry.bookedSkatingSessionId} —{' '}
                {entry.customerName || 'Unknown customer'}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                By {entry.performedByName || 'Unknown'}
              </div>
              {entry.reason && (
                <div className="text-sm text-gray-600 mt-2">
                  Reason: {entry.reason}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default TicketAuditLog;
